import { getCrmPointsRules } from '@/api/public';
import { CrmPointRule, IProductDetail } from '@/api/types/public';
import Tooltip from '@/components/Tooltip';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { accDiv, accMul } from '@/utils/tools';
import { Button } from '@mui/material';
import { QuestionOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ActivityModule from './ActivityModule';
import PointsRule from './dialog/PointsRule';

interface CouponSectionProps {
  showCouponTag: boolean;
  couponTips?: string;
  product: IProductDetail;
  onShowCoupons: () => void;
  isWeb?: boolean;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const CouponSection: React.FC<CouponSectionProps> = ({
  showCouponTag,
  couponTips,
  product,
  onShowCoupons,
  isWeb = false,
  SDProductDetailsPageBtnClick,
}) => {
  const [showPointsDialog, setShowPointsDialog] = useState<boolean>(false);
  const userInfo = useStore((state) => state.userInfo);
  const token = useStore((state) => state.token);
  const clientTokenReady = useStore((state) => state.clientTokenReady);
  const [crmPointRule, setCrmPointRule] = useState<CrmPointRule>({});
  const waiMode = useStore((state) => state.waiMode);
  const currency = useStore((state) => state.currency);
  const t = useTranslations('goodsDetail.coupon');

  // 获取基础价格信息
  const basePriceInfo = useMemo(() => {
    // 会员价
    if (
      userInfo.is_member === 1 &&
      product &&
      product[`price_info_v${userInfo.member_level}`] &&
      product[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      const priceInfo = product[`price_info_v${userInfo.member_level}`].filter((item) => {
        return item.currency === env.NEXT_PUBLIC_DEFAULT_CURRENCY;
      });
      return { price: priceInfo[0].price, sell_price: priceInfo[0].sell_price };
    }
    const priceInfo = product.price_info.filter((item) => {
      return item.currency === env.NEXT_PUBLIC_DEFAULT_CURRENCY;
    });
    return { price: priceInfo[0].price, sell_price: priceInfo[0].sell_price };
  }, [product, userInfo]);

  // 计算积分
  const points = useMemo(() => {
    try {
      if (!crmPointRule.obtain || !clientTokenReady) return 0;
      const { is_member, member_level } = userInfo;
      let multiple_field = 'order_point_multiple'; // 游客
      if (token) {
        // is_member 是否是会员 1是 2否
        multiple_field =
          is_member === 2 ? 'order_point_multiple' : `order_point_multiple_v${member_level}`; // 动态的字段处理
      }
      const multiple = crmPointRule?.obtain?.[multiple_field];
      // 计算积分获取比例
      /* 举例：10美元    4积分 ，计算，1美元多少积分  10/4  */
      const proportion = accDiv(crmPointRule.obtain.ratio, 10);
      // 积分是按照，basePriceInfo 进行处理
      const _points = accMul(basePriceInfo.sell_price, proportion);
      const multiple_points = accMul(_points, multiple);
      return Math.floor(multiple_points);
    } catch (e) {
      logger.log(e);
      return 0;
    }
  }, [product, userInfo, token, crmPointRule, basePriceInfo]);

  useEffect(() => {
    getCrmPointsRules().then(({ code, data }) => {
      if (code === 200) {
        setCrmPointRule(data);
      }
    });
  }, []);

  const ratioNum = useMemo(() => {
    try {
      return Number(crmPointRule?.deduct?.ratio);
    } catch (e) {
      logger.log(e);
      return 0;
    }
  }, [crmPointRule]);

  const renderPointsRule = useCallback(() => {
    return (
      <div>
        <p className={`text-[14px] ${isWeb ? '' : 'mb-10'}`}>
          {t('pointEqualPrice', {
            points: ratioNum,
            pointsText: ratioNum > 1 ? t('points') : t('point'),
            price: formatPriceNumberWithSymbol('1', currency, false),
          })}
        </p>
        <p className="text-[14px]">{t('youCanRedeemThePointsOnYourNextPurchase')}</p>
      </div>
    );
  }, [currency, ratioNum, isWeb, t]);

  // 渲染商品详情标签模块
  const renderCommodityLabels = useCallback(() => {
    if (!product?.commodity_details_label || !Array.isArray(product.commodity_details_label)) {
      return null;
    }

    // 过滤标签（不排序）
    const filteredLabels = product.commodity_details_label.filter((item) => {
      // 基础过滤：状态为1
      if (item.status !== 1) return false;

      // 根据类型进行额外的条件过滤
      switch (item.type) {
        case 'coupon':
          return !!couponTips; // 需要有优惠券提示
        case 'point':
          return points >= 1; // 需要积分大于等于1
        case 'activity':
          return item.scene === 1; // 只处理scene=1的活动
        default:
          return false;
      }
    });

    return filteredLabels.map((item, index: number) => {
      switch (item.type) {
        case 'coupon':
          // 优惠券模块
          return (
            <section
              key={`coupon-${index}`}
              className="h-[20px] px-[8px] rounded-[4px] bg-[#eeebff] text-[#051431] text-[11px] mx-[3px] my-[1px] flex items-center"
              style={{
                fontSize: isWeb ? 'max(.625vw, 11px)' : '',
              }}
              onClick={() => {
                SDProductDetailsPageBtnClick?.('Coupons Label');
              }}
            >
              {couponTips}
            </section>
          );

        case 'point':
          // 积分模块
          return (
            <section
              key={`point-${index}`}
              onClick={() => {
                if (!isWeb) {
                  setShowPointsDialog(true);
                }
                SDProductDetailsPageBtnClick?.('Points');
              }}
              style={{
                fontSize: isWeb ? 'max(.625vw, 11px)' : '',
              }}
              className="h-[20px] px-[8px] rounded-[4px] bg-[#eeebff] text-[#051431] text-[11px] cursor-pointer flex items-center mx-[3px] my-[1px]"
            >
              {isWeb && (
                <Tooltip
                  variant="light"
                  placement="top"
                  title={renderPointsRule()}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: '290px',
                    },
                  }}
                >
                  <span className="flex items-center">
                    {t('earnPoints', {
                      points,
                      pointsText: points > 1 ? t('points') : t('point'),
                    })}
                    <QuestionOutlined className="ml-4 text-[11px] text-[#051314]" />
                  </span>
                </Tooltip>
              )}
              {!isWeb && (
                <>
                  {t('earnPoints', {
                    points,
                    pointsText: points > 1 ? t('points') : t('point'),
                  })}
                  <QuestionOutlined className="ml-4 text-[11px] text-[#a9a9a9] " />
                </>
              )}
            </section>
          );

        case 'activity':
          // 活动模块：移动端和PC端全部展示
          return (
            <ActivityModule
              key={`activity-${index}`}
              item={item}
              SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
            />
          );

        default:
          return null;
      }
    });
  }, [product, couponTips, points, isWeb, renderPointsRule, SDProductDetailsPageBtnClick]);

  if (!showCouponTag) return null;
  return (
    <div className={`flex justify-between items-center ${isWeb ? 'mt-12' : 'pb-[6px] mt-[5px]'}`}>
      <div className={`flex flex-wrap ml-[-3px] ${isWeb ? 'w-[380px]' : 'flex-nowrap'}`}>
        {renderCommodityLabels()}
      </div>
      {couponTips && (
        <Button
          size="small"
          onClick={() => {
            SDProductDetailsPageBtnClick?.('Coupons');
            onShowCoupons();
          }}
          className="normal-case text-[12px]"
          endIcon={<RightArrowOutlined className={`${isWeb ? '!text-[12px]' : ''}`} />}
          sx={
            isWeb
              ? {
                  backgroundColor: colors.primaryPurple,
                  color: colors.primaryWhite,
                  fontSize: '12px',
                  height: '20px',
                  padding: '0px 5px',
                  '& .MuiButton-endIcon': {
                    color: colors.primaryWhite,
                    fontSize: '10px',
                    marginLeft: '2px',
                  },
                }
              : {
                  padding: '0px',
                  color: waiMode ? colors.primaryBlack : '#808080',
                  fontSize: '3.2vw',
                  '& .MuiButton-endIcon': {
                    marginLeft: '4px',
                    fontSize: '14px',
                    color: '#808080',
                  },
                }
          }
        >
          {t('coupons')}
        </Button>
      )}
      <PointsRule
        show={showPointsDialog}
        onClose={() => setShowPointsDialog(false)}
        renderPointsRule={renderPointsRule}
      />
    </div>
  );
};

export default CouponSection;
